//@ts-check

import {
  ApplicationFieldEnum as Fields,
  StudentFieldEnum as StudentFields,
} from 'Common/FieldEnum';
import _ from 'lodash';
import { diff } from 'deep-diff';
import {
  APPLICATION_HUB_CONTACT_INFO_FORM_NAME,
  APPLICATION_HUB_APP_INFO_FORM_NAME,
  APPLICATION_HUB_HEADER_FORM_NAME,
} from 'Common/FormEnum';
import { EMPTY_INCOME } from './Constants';
import moment from 'moment';

const changedFields = [
  Fields.IsDeclinedBenefits,
  Fields.Income1,
  Fields.Income2,
  Fields.Income3,
  Fields.Income4,
  Fields.Freq1,
  Fields.Freq2,
  Fields.Freq3,
  Fields.Freq4,
  Fields.IsFoster,
  Fields.IsKinGap,
  Fields.IsHeadStart,
  Fields.CaseTypeId,
  Fields.HMRCategory,
  Fields.FamilySize,
];

export const MemberTypeEnum = {
  HOH: 1,
  Student: 2,
  Member: 3,
};

/**
 * Gets the member type (`MemberTypeEnum`) of the passed in `id`
 * @param {String} id (String) Student or Member Id
 * @returns {Number} `MemberTypeEnum`
 */
export const getMemberType = (id) => {
  if (!id) {
    return MemberTypeEnum.HOH;
  }
  if (id.indexOf('-') !== -1 || id.indexOf('member_') !== -1) {
    return MemberTypeEnum.Member;
  } else if (id.length > 0) {
    return MemberTypeEnum.Student;
  }
};

/**
 * Determines whether the student or member counts have changed by
 * comparing the previous copy of the application the current
 *
 * @param {Object} prevTempApplication Previous copy of `tempApplication`
 * @param {Object} tempApplication Current copy of `tempApplication`
 * @returns {Boolean} Whether or not the counts have changed
 */
export const haveStudentsOrMemberCountsChanged = (
  prevTempApplication,
  tempApplication
) => {
  let prevCount = 0;
  let currCount = 0;

  if (prevTempApplication.Members !== undefined) {
    prevCount += prevTempApplication.Members.length;
  }

  if (tempApplication.Members !== undefined) {
    currCount += tempApplication.Members.length;
  }

  if (prevTempApplication.Students !== undefined) {
    prevCount += prevTempApplication.Students.length;

    //check for emancipated minors, prev
    const found = prevTempApplication.Students.find((x) =>
      _.get(x, 'Exceptions.IsEmancipatedMinor', false)
    );

    if (found !== undefined) {
      prevCount -= 1;
    }
  }

  if (tempApplication.Students !== undefined) {
    currCount += tempApplication.Students.length;

    //check for emancipated minors, curr
    const found = tempApplication.Students.find((x) =>
      _.get(x, 'Exceptions.IsEmancipatedMinor', false)
    );

    if (found !== undefined) {
      currCount -= 1;
    }
  }

  return prevCount !== currCount;
};

/**
 * Returns the size of the family on the given `tempApplication`
 * @param {Object} tempApplication The current application
 * @returns {Number} The family size
 */
export const getFamilySize = (tempApplication) => {
  let count = 0;

  if (tempApplication.Members !== undefined) {
    count += tempApplication.Members.length;
  }

  if (tempApplication.Students !== undefined) {
    count += tempApplication.Students.length;

    const found = tempApplication.Students.find(
      (x) => x.Exceptions.IsEmancipatedMinor
    );

    if (found !== undefined) {
      count -= 1;
    }
  }

  if (tempApplication.ApplicationInformation !== undefined) {
    if (count > tempApplication.ApplicationInformation.FamilySize) {
      return count;
    } else {
      return tempApplication.ApplicationInformation.FamilySize;
    }
  }
  return count;
};

export const getCalculatedFamilySize = (tempApplication) => {
  let count = 0;

  if (tempApplication.Members !== undefined) {
    count += tempApplication.Members.length;
  }

  if (tempApplication.Students !== undefined) {
    count += tempApplication.Students.length;

    const found = tempApplication.Students.find((x) =>
      _.get(x, 'Exceptions.IsEmancipatedMinor', false)
    );

    if (found !== undefined) {
      count -= 1;
    }
  }
  return count;
};

export const setCaseTypeIdAfterDcOrCaseNumberChange = (
  dc,
  caseNumber,
  change
) => {
  if (!dc && (!caseNumber || caseNumber.length === 0)) {
    change(`Exceptions.${Fields.CaseTypeId}`, null);
  }
};

export const modifyMembersForEmancipatedMinor = (students, members) => {
  const foundEmancipated = students.find(
    (x) => x.Exceptions && x.Exceptions.IsEmancipatedMinor === true
  );
  let output = {
    members: members ? members : [],
    students,
  };

  let hoh = members.find((x) => x.IsHOH === true);

  if (foundEmancipated) {
    // see if there is an existing HOH

    if (hoh) {
      //remove existing
      output.members = members.filter((x) => x.IsHOH === false);
    }
    hoh = {
      Id: null,
      EmancipatedStudentId: foundEmancipated.Id,
      FirstName: foundEmancipated.FirstName,
      LastName: foundEmancipated.LastName,
      SSN: foundEmancipated.SSN,
      LanguageId: foundEmancipated.LanguageId,
      IsHOH: true,
      Income: null,
    };
    output.members.push(hoh);

    //both of these go on the HOH only, but they are on the form
    //in the emancipated minor scenario so get temporarily put on the student payload
    //remove to avoid confusion
    output.students.forEach((student) => {
      delete student.SSN;
      delete student.LanguageId;
    });
  } else {
    //remove any HOH's that are attached to a non existant
    //emancipated student
    //make sure HOH's emancipated still exists
    let shouldRemoveHOH = false;
    if (hoh && hoh.EmancipatedStudentId) {
      shouldRemoveHOH =
        output.students.find(
          (x) =>
            x.Id === hoh.EmancipatedStudentId &&
            x.Exceptions.IsEmancipatedMinor !== true
        ) !== null;
    }

    if (shouldRemoveHOH) {
      output.members = output.members.filter((x) => !x.IsHOH);
    }
  }

  return output;
};

/**
 * Returns the Icon name string based on the type of
 * the Student/Member with the given `id`
 * @param {String} id (String) Student or Member Id
 * @returns {String} Icon name
 */
export const getIconNameFromId = (id) => {
  const memberType = getMemberType(id);

  switch (memberType) {
    case MemberTypeEnum.HOH:
      return 'home';
    case MemberTypeEnum.Member:
      return 'user outline';
    case MemberTypeEnum.Student:
      return 'student';

    default:
      return null;
  }
};

export const navigateToEditBasedOnId = (id, familyId, history) => {
  const queryString = _.get(history, 'location.search', '');

  switch (getMemberType(id)) {
    case MemberTypeEnum.HOH:
      history.push(`/manageapplication/${familyId}/hoh${queryString}`);
      break;
    case MemberTypeEnum.Member:
      history.push(`/manageapplication/${familyId}/member/${id}${queryString}`);
      break;
    case MemberTypeEnum.Student:
      history.push(
        `/manageapplication/${familyId}/student/${id}${queryString}`
      );
      break;
    default:
      return null;
  }
};

export const orderMembersByType = (data) => {
  return _.orderBy(data, [(x) => getMemberType(x.Id)], ['asc']);
};

/**
 * Determines whether a recalculation of the application (via the API)
 * is needed based on what has changed
 * @param {Object} oldApp
 * @param {Object} newApp
 * @return {Boolean} Whether or not the application needs to be
 * recalculated based on the difference between `oldApp` and
 * `newApp`
 */
export const getShouldRecalculateApp = (oldApp, newApp) => {
  const result = diff(oldApp, newApp);
  if (result) {
    const modified = result
      .reduce((a, b) => {
        //Special case for CaseTypeId as 0 and null
        //both imply no CaseTypeId
        if (
          b.path.includes(Fields.CaseTypeId) &&
          b.lhs === 0 &&
          b.rhs === null
        ) {
          return a;
        }

        return a.concat(b.path);
      }, [])
      .find((d) => changedFields.includes(d));

    const memberDiff = result.find(
      (x) => x.path.includes(Fields.Members) && ['A', 'D'].includes(x.kind)
    );

    return modified !== undefined || memberDiff !== undefined;
  }
  return false;
};

/**
 * Determines if any students in the passed in array have
 * case information
 * @param {Array} students
 * @returns {Boolean} Whether any students in the passed
 * in array have case information
 */
export const getCaseInfoExists = (students = []) => {
  return (
    students.filter((s) => {
      return (
        _.get(s, [Fields.Exceptions, Fields.IsDirectCertified], false) ||
        _.get(s, [Fields.Exceptions, Fields.CaseNumber], '') ||
        _.get(s, [Fields.Exceptions, Fields.CaseTypeId], '') ||
        _.get(s, [Fields.Exceptions, Fields.IsExtendedBenefits], false)
      );
    }).length > 0
  );
};

export const getPropertyFromFormName = {
  [APPLICATION_HUB_CONTACT_INFO_FORM_NAME]: Fields.HouseholdContactInfo,
  [APPLICATION_HUB_APP_INFO_FORM_NAME]: Fields.ApplicationInformation,
  [APPLICATION_HUB_HEADER_FORM_NAME]: Fields.ApplicationInformation,
};

/**
 * Sets the income field to a new instance of `EMPTY_INCOME`
 * on the Student or Member with the passed in `id`
 * @param {Object} state
 * @param {String} id
 * @returns {Object}
 */
export const getStateAfterRemovingIncome = (state, id) => {
  let nextState = { ...state };

  switch (getMemberType(id)) {
    case MemberTypeEnum.HOH:
      nextState.tempApplication.Members = [
        ...state.tempApplication.Members.map((member) => {
          if (!member.Id) {
            member[Fields.Income] = { ...EMPTY_INCOME };
          }

          return member;
        }),
      ];

      nextState.shouldRecalculateApp = true;

      break;
    case MemberTypeEnum.Member:
      nextState.tempApplication.Members = [
        ...state.tempApplication.Members.map((member) => {
          if (member.Id === id) {
            member[Fields.Income] = { ...EMPTY_INCOME };
          }

          return member;
        }),
      ];

      nextState.shouldRecalculateApp = true;

      break;
    case MemberTypeEnum.Student:
      nextState.tempApplication.Students = [
        ...state.tempApplication.Students.map((student) => {
          if (student.Id === id) {
            student[Fields.Income] = { ...EMPTY_INCOME };
          }

          return student;
        }),
      ];

      nextState.shouldRecalculateApp = true;

      break;
    default:
      break;
  }

  return nextState;
};

/**
 * Gets the state after clearing the case information from all
 * students on the application. Resets `IsDirectCertified`,
 * `CaseNumber`, `CaseTypeId`, `IsExtendendBenefits` and
 * `IsExtendedBenefitsHasChanged`.
 * @param {Object} state
 * @returns {Object} The state after clearing case information
 * from all students on the application
 */
export const getStateAfterClearingCaseInfo = (state) => {
  const nextState = {
    ...state,
    tempApplication: {
      ...state.tempApplication,
      Students: state.tempApplication.Students.map((student) => ({
        ...student,
        Exceptions: {
          ...student.Exceptions,
          [Fields.IsDirectCertified]: false,
          [Fields.CaseNumber]: null,
          [Fields.CaseTypeId]: null,
          [Fields.IsExtendedBenefits]: false,
          [Fields.IsExtendedBenefitsHasChanged]: false,
        },
      })),
    },
  };

  nextState.shouldRecalculateApp = getShouldRecalculateApp(
    state.tempApplication,
    nextState.tempApplication
  );

  return nextState;
};

/**
 * Gets the state after modifying any errors on the `payload`
 * object and assigning them to the `submitErrors` property.
 * Removes 'ApplicationInformation.' and 'HouseholdContactInfo.'
 * from the `Title` properties on the errors.
 * @param {Object} state
 * @param {Object} payload
 * @returns {Object} The state after modifying the errors
 * on the `payload` object and assigning them to the
 * `submitErrors` property
 */
export const getStateWithModifiedSubmitErrors = (state, payload) => {
  if (_.has(payload, 'response.data.Errors')) {
    const submitErrors = payload.response.data.Errors.map((error) => {
      return {
        ...error,
        Title: _.get(error, 'Title', '')
          .replace('ApplicationInformation.', '')
          .replace('HouseholdContactInfo.', '')
          .replace('Students', 'Error')
          .replace('Members', 'Error'),
      };
    });

    return {
      ...state,
      submitErrors,
    };
  }

  return {
    ...state,
    submitErrors: [
      {
        Title: 'Error',
        Message:
          'An error occurred while saving this application. Please try again.',
      },
    ],
  };
};

export const formatHistoryDataDates = (data) => {
  return data.map((element) => {
    return {
      ...element,
      OccurenceDT: moment(element.OccurenceDT).format('MM/DD/YYYY h:mm:ss A'),
    };
  });
};

export const hasOneDCStudent = (students) => {
  return students.some((s) => s.Exceptions && s.Exceptions.IsDirectCertified);
};

export const convertStudentForApplication = (studentData) => {
  const convertedStudent = {
    [Fields.BirthDate]: studentData[StudentFields.BirthDate],
    [Fields.CaseNumber]: studentData[StudentFields.CaseNumber],
    [Fields.CaseTypeId]: studentData[StudentFields.CaseTypeId],
    [Fields.DeniedReason]: studentData[StudentFields.DeniedReason],
    [Fields.EffectiveDate]: studentData[StudentFields.EffectiveDate],
    [Fields.EffectiveDateHasChanged]: false,
    [Fields.EthnicityId]: studentData[StudentFields.EthnicityId],
    [Fields.Exceptions]: {
      [Fields.CaseNumber]: studentData[StudentFields.CaseNumber],
      [Fields.CaseTypeId]: studentData[StudentFields.CaseTypeId],
      [Fields.HMRCategory]: studentData[StudentFields.HMRCategory],
      [Fields.IsDirectCertified]: studentData[StudentFields.DirectCertified],
      [Fields.IsEmancipatedMinor]: studentData[StudentFields.EmancipatedMinor],
      [Fields.IsExtendedBenefits]: studentData[StudentFields.ExtendedBenefits],
      [Fields.IsExtendedBenefitsHasChanged]: false,
      [Fields.IsFoster]: studentData[StudentFields.FosterChild],
      [Fields.IsHeadStart]: studentData[StudentFields.HeadStart],
      [Fields.IsKinGap]: studentData[StudentFields.KinGAP],
    },
    [Fields.FirstName]: studentData[StudentFields.FirstName],
    [Fields.GradeId]: studentData[StudentFields.GradeId],
    [Fields.HHFirstName]: studentData[StudentFields.HHFirstName],
    [Fields.HHLastName]: studentData[StudentFields.HHLastName],
    [Fields.Id]: studentData[StudentFields.Id],
    [Fields.IdSeed]: studentData[StudentFields.IdSeed],
    [Fields.Income]: {
      [Fields.Freq1]: -1,
      [Fields.Freq2]: -1,
      [Fields.Freq3]: -1,
      [Fields.Freq4]: -1,
      [Fields.Id]: 0,
      [Fields.Income1]: 0,
      [Fields.Income2]: 0,
      [Fields.Income3]: 0,
      [Fields.Income4]: 0,
    },
    [Fields.IsNewToApp]: true,
    [Fields.LastName]: studentData[StudentFields.LastName],
    [Fields.MiddleName]: studentData[StudentFields.MiddleName],
    [Fields.RaceId]: studentData[StudentFields.RaceId],
    [Fields.ServingNumber]: studentData[StudentFields.ServingNum],
    [Fields.SiteId]: studentData[StudentFields.SiteId],
    [Fields.StatusBeforeVerification]: null,
    [Fields.StatusChangeType]: 0,
    [Fields.StatusCurrent]: studentData[StudentFields.StatusCurrent],
    [Fields.StatusFuture]: studentData[StudentFields.StatusFuture],
    [Fields.Teacher]: studentData[StudentFields.Teacher],
    [Fields.LanguageId]: studentData[StudentFields.LanguageId],
  };

  return convertedStudent;
};



// WEBPACK FOOTER //
// ./src/pages/manageapplication/helpers/Helpers.js